import { useId, useMemo } from 'react'

type ProjectShape = 'tile' | 'diamond' | 'hex' | 'tri' | 'stack' | 'folder'
type ProjectPattern = 'grid' | 'orbit' | 'bars' | 'nodes' | 'ribbon'

type ProjectAvatarDescriptor = {
  shape: ProjectShape
  pattern: ProjectPattern
  hue: number
  hue2: number
  accentHue: number
  cut: number
  rotate: number
}

const SHAPES: Record<ProjectShape, string> = {
  tile: 'M9 8h46v46H9V8Z',
  diamond: 'M32 5 59 32 32 59 5 32 32 5Z',
  hex: 'M23 6h18l16 26-16 26H23L7 32 23 6Z',
  tri: 'M32 5 59 57H5L32 5Z',
  stack: 'M13 9h38l6 11v35H7V20l6-11Z',
  folder: 'M7 17h18l5 7h27v31H7V17Z',
}

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function rand(seed: number) {
  let x = seed || 1
  return () => {
    x |= 0
    x = (x + 0x6d2b79f5) | 0
    let t = Math.imul(x ^ (x >>> 15), 1 | x)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function pick<T>(items: readonly T[], value: number): T {
  return items[Math.floor(value * items.length)] ?? items[0]
}

function describe(projectId: string): ProjectAvatarDescriptor {
  const r = rand(hashString(projectId))
  const hue = Math.floor(r() * 360)
  return {
    shape: pick(['tile', 'diamond', 'hex', 'tri', 'stack', 'folder'] as const, r()),
    pattern: pick(['grid', 'orbit', 'bars', 'nodes', 'ribbon'] as const, r()),
    hue,
    hue2: (hue + 50 + Math.floor(r() * 110)) % 360,
    accentHue: (hue + 170 + Math.floor(r() * 80)) % 360,
    cut: Math.floor(r() * 4),
    rotate: Math.round((r() - 0.5) * 12),
  }
}

function hsl(h: number, s: number, l: number, alpha = 1): string {
  return `hsl(${h} ${s}% ${l}% / ${alpha})`
}

function CutCorners({ kind }: { kind: number }) {
  if (kind === 1) {
    return (
      <>
        <path d="M8 8h12L8 20V8Z" fill="#181825" opacity="0.78" />
        <path d="M56 56H44l12-12v12Z" fill="#181825" opacity="0.6" />
      </>
    )
  }
  if (kind === 2) {
    return (
      <>
        <circle cx="11" cy="53" r="5" fill="#181825" opacity="0.55" />
        <circle cx="53" cy="11" r="5" fill="#181825" opacity="0.35" />
      </>
    )
  }
  if (kind === 3) {
    return (
      <>
        <path d="M7 32h11" stroke="#181825" strokeWidth="5" strokeLinecap="round" opacity="0.65" />
        <path d="M32 57V46" stroke="#181825" strokeWidth="5" strokeLinecap="round" opacity="0.48" />
      </>
    )
  }
  return null
}

function Pattern({
  kind,
  hue,
  accentHue,
}: {
  kind: ProjectPattern
  hue: number
  accentHue: number
}) {
  const accent = hsl(accentHue, 84, 72, 0.92)
  const line = hsl(hue, 88, 82, 0.46)
  if (kind === 'orbit') {
    return (
      <>
        <circle cx="24" cy="38" r="11" fill="none" stroke={line} strokeWidth="4" />
        <circle cx="24" cy="38" r="4" fill={accent} />
        <path d="M11 50 39 22" stroke={line} strokeWidth="4" strokeLinecap="round" />
      </>
    )
  }
  if (kind === 'bars') {
    return (
      <>
        <path d="M12 42h29" stroke={line} strokeWidth="6" strokeLinecap="round" />
        <path d="M12 30h22" stroke={accent} strokeWidth="6" strokeLinecap="round" />
        <path d="M12 18h14" stroke={line} strokeWidth="6" strokeLinecap="round" />
      </>
    )
  }
  if (kind === 'nodes') {
    return (
      <>
        <path d="M17 42 28 27l13 10" stroke={line} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="17" cy="42" r="4" fill={accent} />
        <circle cx="28" cy="27" r="4" fill={hsl(hue, 86, 80)} />
        <circle cx="41" cy="37" r="4" fill={accent} />
      </>
    )
  }
  if (kind === 'ribbon') {
    return (
      <>
        <path d="M7 45C19 35 29 36 41 43c7 4 12 4 17 1v14H7V45Z" fill={hsl(accentHue, 84, 61, 0.78)} />
        <path d="M12 16h24" stroke={line} strokeWidth="5" strokeLinecap="round" />
      </>
    )
  }
  return (
    <>
      <path d="M15 12v36M29 12v36M43 12v36" stroke={line} strokeWidth="3" />
      <path d="M8 24h42M8 38h42" stroke={line} strokeWidth="3" />
      <rect x="15" y="24" width="14" height="14" fill={accent} opacity="0.78" />
    </>
  )
}

/**
 * Stable project identity mark.
 *
 * Unlike PixelPet, this intentionally avoids faces and creature language. A project is
 * rendered as a geometric workspace token, seeded by project_id so renames do not
 * change its identity.
 */
export function ProjectAvatar({
  projectId,
  label,
  size = 26,
}: {
  projectId: string
  label?: string | null
  size?: number
}) {
  const rawId = useId()
  const id = rawId.replace(/[^a-zA-Z0-9_-]/g, '')
  const d = useMemo(() => describe(projectId), [projectId])
  const shape = SHAPES[d.shape]

  return (
    <span className="project-avatar" style={{ width: size, height: size }} title={label ?? projectId}>
      <svg
        viewBox="0 0 64 64"
        width={size}
        height={size}
        role="img"
        aria-label={`${label ?? 'Project'} project avatar`}
      >
        <defs>
          <linearGradient id={`${id}-project`} x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor={hsl(d.hue, 70, 68)} />
            <stop offset="1" stopColor={hsl(d.hue2, 78, 45)} />
          </linearGradient>
          <clipPath id={`${id}-clip`}>
            <path d={shape} />
          </clipPath>
        </defs>
        <g transform={`rotate(${d.rotate} 32 32)`} style={{ transformOrigin: '32px 32px' }}>
          <path d={shape} fill={`url(#${id}-project)`} />
          <g clipPath={`url(#${id}-clip)`}>
            <circle cx="13" cy="12" r="18" fill="rgba(255,255,255,.16)" />
            <circle cx="52" cy="54" r="20" fill="rgba(17,17,27,.22)" />
            {/*
              The pattern layer is strokes unless a shape opts into a fill. Without this,
              SVG's default `fill: black` applies: straight-line paths hide it, but the
              `nodes` polyline encloses a triangle and painted a solid black wedge over
              one project in five. Children that set their own fill still win.
            */}
            <g fill="none">
              <Pattern kind={d.pattern} hue={d.hue2} accentHue={d.accentHue} />
            </g>
            <path d="M47 5h20v22H47V5Z" fill="#181825" opacity="0.22" />
          </g>
          <g fill="none">
            <CutCorners kind={d.cut} />
          </g>
          <path d={shape} fill="none" stroke="#181825" strokeWidth="4.5" />
          <path d={shape} fill="none" stroke="rgba(255,255,255,.22)" strokeWidth="1.5" />
        </g>
      </svg>
    </span>
  )
}
